/**
 * What a framed Chat42 tells its host, and when, decided without a frame (ADR 0097).
 *
 * The frame app (`apps/web/src/main.tsx`) renders as it is imported, so the choices it makes
 * about its host are made here, as pure functions, where a test can reach them: whether the empty
 * screen should say "Connecting…", when a conversation's standing is worth asking for again, and
 * the shapes of the two messages that carry it. Shapes and nothing else: the frame decides
 * nothing about a session, the runtime does, and this only says it.
 *
 * @module chat42/react/host-frame
 */

/**
 * What a frame does once it has looked for a session a host stored the id of (ADR 0135).
 *
 * `failed`: the check itself failed, so nothing is made and the host is told, as always.
 * `missing`: a read-only open of an id that is not there, so nothing is made and the host is told
 * `session-missing`. `create`: an ordinary open of an id that is gone, made again as before.
 * `show`: a read-only open found it, and it is shown with nothing written to it. `attach`: an
 * ordinary open found it, and it is brought up to date (about, code) as before.
 *
 * **A finished conversation is not opened to be spoken in** (ADR 0151). An ordinary open of one that
 * is done or abandoned (`closed`) is `create`, as a gone one is: with the host's origin that is
 * find-or-create, so the person reaches the live conversation under the record's name, or a new one
 * under it. Opened as it was, it took the person's message and refused it ("Session is done"): the
 * host had stored the id of a conversation Start over, a cleanup or a fresh second look had since
 * finished, and nothing pointed on from it. A read-only open still shows it: a replay is of what
 * happened there.
 */
export type ReopenStep = 'failed' | 'missing' | 'create' | 'show' | 'attach';
export function reopenStep(found: 'found' | 'absent' | 'failed', readOnly: boolean, closed = false): ReopenStep {
  if (found === 'failed') return 'failed';
  if (found === 'absent') return readOnly ? 'missing' : 'create';
  if (readOnly) return 'show';
  return closed ? 'create' : 'attach';
}

/** Done or abandoned: finished for good, and refusing a message (the runtime's `isClosed`). */
export const closedState = (state: unknown): boolean => state === 'done' || state === 'abandoned';

/**
 * Whether the host is told of a session's change (ADR 0151). While an ordinary (not read-only)
 * `open-session` is still being decided, a done or abandoned conversation is not reported: the
 * stored id is in the fleet the moment it is looked up, and Rule42 takes the first report after
 * an open as its answer, so reporting the finished one made it keep that id and ignore the live
 * one the open then reached. Once the open is answered, or for a read-only open (a replay shows
 * the finished one as it is), every change is reported.
 */
export interface OpenGate {
  /** An `open-session` arrived; `readOnly` is whether it asked to replay. */
  opening(readOnly: boolean): void;
  /** The host's request is answered, or another kind of request replaced it. */
  settle(): void;
  /** Whether this session's change goes to the host now. */
  allows(session: { state: string }): boolean;
}
export function openGate(): OpenGate {
  let deciding = false;
  return {
    opening: (readOnly) => { deciding = !readOnly; },
    settle: () => { deciding = false; },
    allows: (session) => !(deciding && closedState(session.state)),
  };
}

/** Where the host's request for a session has got to. */
export type HostRequest =
  /** Signed in, and the host has not asked for a session yet. */
  | 'none'
  /** Asked for: being opened, found or made. */
  | 'pending'
  /** Answered, one way or the other. */
  | 'done';

/**
 * The surfaces a framed Chat42 draws: the host's, over the frame's own defaults for a frame.
 *
 * One default differs from the app's: `account` is off. In a frame the person's identity is the
 * token the host handed over, and the host's own header already says who that is, so Chat42's
 * chip was a second, unrequested answer. It was also drawn floating at the frame's top right,
 * over the close control of whatever panel was docked there (2026-10-18: the owner could not
 * close a document in rule42-cloud's AI panel). A host that wants it back says `account: true`.
 *
 * A default rather than something the frame forces, because the host is the one that knows
 * whether its page names the person; the host's word wins over it, in both directions.
 *
 * And `sideRailOpen` is off (ADR 0104): the side rail starts as its strip of icons, because a
 * host's panel is narrow and the summary would lie over the conversation in it. The person opens
 * it with a click, and the frame remembers that they did. A host that wants it open first says
 * `sideRailOpen: true`; one that wants no rail says `sideRail: false`.
 */
export function frameSurfaces(
  host: Readonly<Record<string, boolean>> | undefined,
): Record<string, boolean> {
  return { account: false, sideRailOpen: false, ...host };
}

/**
 * Whether the empty screen should say "Connecting…" rather than "No conversation open."
 *
 * While the host's request is on its way, always: it was in exactly that window that the owner
 * was offered "Start a new session" beside the session already being made. Before the host has
 * asked, only where it turned the `new` surface off, because such a host always asks and has
 * said it makes the session itself; a host that left it on may be one that never asks, and its
 * empty screen is the way in.
 */
export function hostConnecting(request: HostRequest, surfaces: Readonly<Record<string, boolean>> | undefined): boolean {
  if (request === 'pending') return true;
  return request === 'none' && surfaces?.['new'] === false;
}

/**
 * Whether a change of state is a turn starting or ending, which is when the number of turns
 * and the model can have moved. Asked on both edges so a host's starters go as the person's
 * first turn begins, not only once it has been answered.
 *
 * Busy is running or queued (2026-10-19). A turn quick enough to finish between two reads of the
 * session list is seen as queued and then at rest, never running, and with running alone as the
 * edge the host was never told it had ended: Rule42's "the AI is working on this" spun on.
 */
const busy = (state: string): boolean => state === 'running' || state === 'queued';
export function turnMoved(was: string | undefined, now: string): boolean {
  if (was === undefined) return false;
  return busy(was) !== busy(now);
}

/**
 * `chat42:needs-attention` for the session on screen, when whether it waits on the person changes
 * (ADR 0141): `count` 1 as it starts waiting (an approval to give, a question to answer, a sign-in),
 * 0 as it stops. Nothing when it has not changed, or for a session the host has not been shown yet,
 * so a host that keeps a tucked-away panel can put a dot on its button and take it off again, and
 * never has to read states it does not know. The protocol has carried the message since version 1;
 * nothing sent it until now.
 */
export function attentionMessage(was: string | undefined, now: string): { type: 'chat42:needs-attention'; count: number } | null {
  const waiting = (state: string | undefined) => state === 'waiting:human';
  if (was === undefined || waiting(was) === waiting(now)) return null;
  return { type: 'chat42:needs-attention', count: waiting(now) ? 1 : 0 };
}

/** Where a conversation stands, as `GET /api/sessions/:id/standing` answers. */
export interface Standing {
  model: string | null;
  turns: number;
}

/**
 * What the frame knows of each session's standing, where the latest answer wins.
 *
 * Standing is read on open, as each turn starts and ends, and by start-work after it changes
 * the model, and those reads overlap. A read begun before start-work moved the session to the
 * person's model could answer after it, and the frame would then tell the host, and keep, the
 * old model. So each read is numbered as it begins, and an answer is kept only if nothing was
 * begun or set after it. What start-work says is set directly, and outranks any read begun
 * before it.
 */
export interface StandingKeeper {
  get(sessionId: string): Standing | undefined;
  /** A read is beginning. The function keeps its answer, and says whether it did. */
  begin(sessionId: string): (standing: Standing) => boolean;
  /** Known now, without a read: everything begun before this is stale. */
  set(sessionId: string, standing: Standing): void;
}

export function standingKeeper(): StandingKeeper {
  const held = new Map<string, Standing>();
  const latest = new Map<string, number>();
  const next = (id: string) => {
    const n = (latest.get(id) ?? 0) + 1;
    latest.set(id, n);
    return n;
  };
  return {
    get: (id) => held.get(id),
    begin: (id) => {
      const mine = next(id);
      return (standing) => {
        if (latest.get(id) !== mine) return false;
        held.set(id, standing);
        return true;
      };
    },
    set: (id, standing) => {
      next(id);
      held.set(id, standing);
    },
  };
}

/**
 * `chat42:session-changed` for one session: its id, title and state, and its standing where it
 * is known. Left out where it is not, rather than sent as a guess: a host reads a missing
 * `turns` as "not known yet", and a zero it could not tell from a real one would offer starters
 * in a conversation that has begun.
 */
export function sessionChangedMessage(
  session: { id: string; title: string; state: string },
  standing?: Standing,
): {
  type: 'chat42:session-changed'; sessionId: string; title: string; state: string; model?: string | null; turns?: number;
} {
  return {
    type: 'chat42:session-changed',
    sessionId: session.id,
    title: session.title,
    state: session.state,
    ...(standing ? { model: standing.model, turns: standing.turns } : {}),
  };
}

/**
 * `chat42:error`, with the attach case and repository where the error is about code that could
 * not be attached, so a host can say it in its own words and need never show `message`.
 */
export function frameErrorMessage(
  message: string,
  trouble?: { case: string; repository?: string },
): { type: 'chat42:error'; message: string; case?: string; repository?: string } {
  return {
    type: 'chat42:error',
    message,
    ...(trouble ? { case: trouble.case } : {}),
    ...(trouble?.repository ? { repository: trouble.repository } : {}),
  };
}

/**
 * The token a host handed over, held where every request reads it (ADR 0101).
 *
 * A framed client used to be built with `token: () => token` over the token `init` carried, so
 * it presented that one token until the frame was remounted, and `chat42:token` had nowhere to
 * go. A turn running past the token's quarter of an hour then failed in the middle, and the
 * organisation's library, which the server reads with a token it holds, was never given one at
 * all. So the token lives here, a renewal replaces it, and the client reads it per request.
 */
export interface HostToken {
  /** The newest token the host has handed over. */
  current(): string;
  /** A renewal from the host. An empty one is ignored: it would sign the frame out, not in. */
  renew(token: string): boolean;
}

export function hostToken(initial: string): HostToken {
  let held = initial;
  return {
    current: () => held,
    renew: (token) => {
      if (typeof token !== 'string' || !token.trim() || token === held) return false;
      held = token;
      return true;
    },
  };
}

/**
 * The token options a framed `Chat42Client` is built with: the host's token as its bearer, and
 * as its gateway and MCP tokens too.
 *
 * The second half is the point. A request with no `x-chat42-mcp-token` leaves the server
 * holding nothing for the person, and the organisation's library and every turn that outlives
 * its request read the held one. The host's token is the one identity a frame has, and the
 * server already falls back to it for both audiences on each request, so naming it under both
 * changes nothing about which token is sent where; it makes the server keep the newest, so a
 * renewal reaches a turn that is already running. A host whose issuer separates audiences
 * would need to hand over each; Rule42's token is accepted by the API, the gateway and the MCP.
 */
export function framedClientOptions(
  token: HostToken,
  /**
   * The host's entry context as it is now. Its `orgId` goes on every request as the host's
   * organisation, read per request because the context may arrive after the client is built,
   * so the Skills screen, the library and billing follow the organisation the host named, as
   * that conversation's turns do (ADR 0103).
   */
  entry?: () => Readonly<Record<string, string>> | undefined,
): {
  token: () => string;
  gatewayToken: () => string;
  mcpToken: () => string;
  hostOrganisation?: () => string | undefined;
  hostProject?: () => string | undefined;
} {
  return {
    token: () => token.current(), gatewayToken: () => token.current(), mcpToken: () => token.current(),
    // Its `projectId` the same way, so the framed Skills screen shows what is in force for the
    // project the host has open, as that conversation's turns are given (ADR 0107).
    ...(entry ? { hostOrganisation: () => entry()?.['orgId'], hostProject: () => entry()?.['projectId'] } : {}),
  };
}

/** What handing a host's work over needs of a client. `Chat42Client` is one; a test's fake is another. */
export interface AskWorkClient {
  ask(sessionId: string, ask: {
    prompt: string; model?: string; preset?: string; permissionMode?: string; focus?: Record<string, string[]>;
    run?: string; repository?: string; from?: 'host' | 'project'; readsCodeOnly?: string;
  }): Promise<{ id: string; queued: boolean; position?: number; merged?: true }>;
}

/** The work a host asked for, as `chat42:start-work` carries it, or as the conversation was told of it. */
export interface HostWork {
  prompt: string;
  /** What the person pressed, drawn by the conversation view in place of the prompt (ADR 0142). */
  label?: string;
  model?: string;
  preset?: string;
  permissionMode?: string;
  /** Already turned into a focus: see `focusForWork`. */
  focus?: Record<string, string[]>;
  /** The run it works, where known: two asks for one run are one (ADR 0117). */
  run?: string;
  /** The repository a project's report is on. */
  repository?: string;
  /** The host's `start-work`, or the conversation told of a run it did not know (ADR 0108). */
  from?: 'host' | 'project';
  /** Its turn reads the code and changes none of it, and why (`StartWork.readsCodeOnly`). */
  readsCodeOnly?: string;
}

/** What a turn that may only read the code says, where the host gave `true` and no words. Kept with the runtime's. */
const READS_CODE_ONLY = 'This request reads the code and changes none of it.';

/** `start-work`'s `readsCodeOnly` as an ask carries it: one line of words, or nothing. */
export function readsCodeOnlyOf(raw: unknown): string | undefined {
  if (raw === true) return READS_CODE_ONLY;
  if (typeof raw !== 'string') return undefined;
  const line = raw.replace(/\s+/g, ' ').trim();
  return line ? line.slice(0, 200) : undefined;
}

/**
 * What a frame tells its host of work it handed over: started, waiting its turn, or let go without
 * starting (Start over took it back, or its start failed). Each names the ask, so a host following
 * several tells them apart (ADR 0137).
 */
export type WorkAnswer =
  | { type: 'chat42:work-started'; sessionId: string; askId?: string }
  | { type: 'chat42:work-queued'; sessionId: string; position: number; merged?: true; askId?: string }
  | { type: 'chat42:work-dropped'; sessionId: string; askId: string; how: 'withdrawn' | 'failed' };

/**
 * Hand a host's work to the conversation's line on the server, and say what became of it
 * (ADR 0137).
 *
 * The frame used to set the session up and send the prompt itself (focus, model, preset,
 * permission mode, then the message), and a project's work waited in a queue kept in the frame.
 * Work that arrived while a turn ran steered that turn, work that arrived while the person had a
 * question open went into the question, and work waiting in the frame was lost when the frame
 * reloaded, the host opened another record, or the panel closed. Now the server holds every piece
 * of work in the conversation's one line and starts each once the conversation is at rest, with
 * its settings applied in that order as it starts. The frame only hands it over and tells its host
 * where it stands: `work-started` when it started at once, `work-queued` with its place when it
 * waits (`followAsks` says when it starts, while the frame is open).
 */
export async function askHostWork(
  client: AskWorkClient,
  sessionId: string,
  work: HostWork,
): Promise<{ id: string; queued: boolean; position?: number; answer: WorkAnswer }> {
  const place = await client.ask(sessionId, {
    prompt: work.prompt,
    ...(work.label ? { label: work.label } : {}),
    ...(work.model ? { model: work.model } : {}),
    ...(work.preset ? { preset: work.preset } : {}),
    ...(work.permissionMode ? { permissionMode: work.permissionMode } : {}),
    ...(work.focus ? { focus: work.focus } : {}),
    ...(work.run ? { run: work.run } : {}),
    ...(work.repository ? { repository: work.repository } : {}),
    ...(work.from ? { from: work.from } : {}),
    ...(work.readsCodeOnly ? { readsCodeOnly: work.readsCodeOnly } : {}),
  });
  const answer: WorkAnswer = place.queued
    ? { type: 'chat42:work-queued', sessionId, position: place.position ?? 1, ...(place.merged ? { merged: true as const } : {}), askId: place.id }
    : { type: 'chat42:work-started', sessionId, askId: place.id };
  return { id: place.id, queued: place.queued, ...(place.position ? { position: place.position } : {}), answer };
}

/**
 * What became of asks this frame handed over and is still waiting to hear of, from where the
 * conversation's asks stand now (ADR 0137): each that moved in the line is told again with its new
 * place, each whose words reached the conversation is told started, and each let go without
 * starting (Start over, a start that failed) is told dropped, never started. A server older than
 * `ended` says nothing of how an ask left: one gone from its line is then taken as started, as before.
 * Pure, so the rule is tested without a frame.
 */
export function followAsks(
  waiting: ReadonlyMap<string, { sessionId: string; position: number }>,
  sessionId: string,
  line: {
    current?: { id: string; started: boolean; position?: number };
    waiting: ReadonlyArray<{ id: string; position?: number }>;
    ended?: ReadonlyArray<{ id: string; how: 'started' | 'withdrawn' | 'failed' }>;
  },
): { told: WorkAnswer[]; still: Map<string, { sessionId: string; position: number }> } {
  const told: WorkAnswer[] = [];
  const still = new Map(waiting);
  for (const [id, was] of waiting) {
    if (was.sessionId !== sessionId) continue;
    const queued = line.waiting.find((a) => a.id === id)
      ?? (line.current?.id === id && !line.current.started ? line.current : undefined);
    if (queued) {
      const position = queued.position ?? 1;
      if (position !== was.position) {
        still.set(id, { sessionId, position });
        told.push({ type: 'chat42:work-queued', sessionId, position, askId: id });
      }
      continue;
    }
    const ended = line.ended?.find((e) => e.id === id);
    const running = line.current?.id === id && line.current.started;
    // Not in the line and not said how it left, by a server that says: still being set up, or
    // gone past what it remembers. Asked again on the next read rather than guessed at.
    if (!ended && !running && line.ended !== undefined) continue;
    still.delete(id);
    told.push(ended && ended.how !== 'started'
      ? { type: 'chat42:work-dropped', sessionId, askId: id, how: ended.how }
      : { type: 'chat42:work-started', sessionId, askId: id });
  }
  return { told, still };
}
